// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { markInvoiceAsSent } from '@/billing/api/mark-invoice-as-sent';
import { InvoiceDetail } from '@/billing/api/types';
import { BILLING_BREADCRUMB_ROOT } from '@/billing/components/billing-nav';
import { DeleteDraftInvoiceDialog } from '@/billing/components/delete-draft-invoice-dialog';
import { DownloadPDFButton } from '@/billing/components/download-pdf-button';
import { EditableLineItemTable } from '@/billing/components/editable-line-item-table';
import {
  AddressBlock,
  BillToBlock,
} from '@/billing/components/invoice-address-blocks';
import { InvoiceAuditDrawer } from '@/billing/components/invoice-audit-drawer';
import { InvoiceEditDialog } from '@/billing/components/invoice-edit-dialog';
import { InlineEditableField } from '@/billing/components/invoice-inline-editable-field';
import { SettlementRow } from '@/billing/components/invoice-settlement-row';
import { MarkInvoiceAsSentDialog } from '@/billing/components/mark-invoice-as-sent-dialog';
import { RecordPaymentDialog } from '@/billing/components/record-payment-dialog';
import { VoidInvoiceDialog } from '@/billing/components/void-invoice-dialog';
import { useInvoice } from '@/billing/hooks/use-invoice';
import { useOpenInvoice } from '@/billing/hooks/use-open-invoice';
import { useRevertInvoiceToDraft } from '@/billing/hooks/use-revert-invoice-to-draft';
import { EditablePhaseDate } from '@/components/editable-phase-date';
import { EntityNotFound } from '@/components/entity-not-found';
import { InvoiceStatusChip } from '@/components/invoice-status-chip';
import { KnownErrorSnackbar } from '@/components/known-error-snackbar';
import { Link } from '@/components/link';
import { MultiTimezoneTooltip } from '@/components/multi-timezone-tooltip';
import { Phase, PhaseIndicator } from '@/components/phase-indicator';
import { SectionBreadcrumbs } from '@/components/section-breadcrumbs';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  EditRounded,
  NavigateNextRounded,
} from '@/constants/hardfin-standard-icons';
import { InvoiceStatus } from '@/constants/invoice-status';
import {
  formatAmountWithCurrency,
  formatCurrencyValue,
} from '@/currency/utilities/format';
import { subCurrency } from '@/currency/utilities/math';
import { useDisclosure } from '@/hooks/use-disclosure';
import { getPaymentTerms } from '@/payment-term/api/get-payment-terms';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute('/_app/billing/invoices/$invoiceKey')({
  component: InvoiceDetailRoute,
});

// ---------------------------------------------------------------------------
// Phase indicator config
// ---------------------------------------------------------------------------

// The phases array holds only the linear states; 'void' is a sentinel that
// lives outside the array so the tracker can mark it as the current (and
// terminal) status without treating any of the lifecycle phases as active.
// This mirrors how FulfillmentPhaseIndicator handles 'cancelled' — the
// cancelled pseudo-phase is not rendered inline; it only triggers the
// red-X terminal treatment + "Void" label on the final slot with a
// muted text.secondary color, matching the FO cancelled visual.
type InvoicePhase = 'draft' | 'open' | 'sent' | 'paid' | 'void';

const TERMINAL_PHASES: InvoicePhase[] = ['paid', 'void'];

function statusToPhase(status: InvoiceStatus, isSent: boolean): InvoicePhase {
  switch (status) {
    case 'DRAFT':
      return 'draft';
    case 'OPEN':
      return isSent ? 'sent' : 'open';
    case 'PARTIALLY_PAID':
    case 'PAID':
      return 'paid';
    case 'VOID':
      return 'void';
    default:
      return 'draft';
  }
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatDate(dt: DateTime): string {
  return dt.toLocaleString({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function InvoiceDetailRoute() {
  const { invoiceKey } = Route.useParams();
  const { invoice, isLoading, error } = useInvoice(invoiceKey);

  // Prefetch payment terms here so the edit dialog can read straight from
  // the cache when it opens. The dialog is mounted at all times via the
  // useDisclosure pattern, so this query is also always live.
  useQuery({
    queryKey: ['payment-term'],
    queryFn: getPaymentTerms,
  });

  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Stack alignItems="center" sx={{ mt: 6 }}>
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  if (error || !invoice) {
    return (
      <Container maxWidth="xl">
        <EntityNotFound
          entityType="invoice"
          listPath="/billing/invoices"
          listLabel="Go to invoices"
        />
      </Container>
    );
  }

  return <InvoiceDetailContent invoice={invoice} />;
}

// Codes that mean the action already happened (e.g. another tab opened
// the invoice while this dialog was showing). Treat these as success —
// close the dialog and refresh the data.
const IDEMPOTENT_OPEN_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_already_open,
]);
const IDEMPOTENT_REVERT_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_already_draft,
]);
const IDEMPOTENT_VOID_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_already_void,
]);

// Status-level known errors surfaced via the known-error snackbar (user-
// facing, no Sentry). Distinct from idempotent codes, which are treated as
// a silent success.
const KNOWN_RECORD_PAYMENT_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_not_payable,
]);
const KNOWN_VOID_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_not_voidable,
  ERROR_CODE_MAPPING.invoice_has_settlements,
]);
const KNOWN_DELETE_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_not_deletable,
]);

interface InvoiceDetailContentProps {
  invoice: InvoiceDetail;
}

function InvoiceDetailContent(props: InvoiceDetailContentProps) {
  const { invoice } = props;

  const navigate = useNavigate();
  const editDialog = useDisclosure();
  const auditDrawer = useDisclosure();
  const openConfirmDialog = useDisclosure();
  const revertConfirmDialog = useDisclosure();
  const voidDialog = useDisclosure();
  const deleteDialog = useDisclosure();
  const recordPaymentDialog = useDisclosure();
  const markAsSentDialog = useDisclosure();
  const isSent = invoice.firstSentAt !== null;
  const currentPhase = statusToPhase(invoice.status, isSent);
  const queryClient = useQueryClient();

  // Mirrors the shipment phase indicator: every phase date renders its
  // short form with a MultiTimezoneTooltip so hovering reveals the full
  // timestamp in local / org / UTC. Sent gets an inline edit icon once it
  // has been stamped, matching the shipments "shipped date" pattern.
  const handleSaveSentAt = useCallback(
    async (next: DateTime) => {
      await markInvoiceAsSent({ invoiceKey: invoice.key, sentAt: next });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['invoices', 'detail', invoice.key],
        }),
        queryClient.invalidateQueries({ queryKey: ['invoices'] }),
      ]);
    },
    [invoice.key, queryClient]
  );

  const phases: Phase<InvoicePhase>[] = useMemo(
    () => [
      {
        status: 'draft',
        label: 'Draft',
        number: 1,
        subtext: (
          <MultiTimezoneTooltip
            timestamp={invoice.draftedAt}
            placement="bottom"
          >
            {formatDate(invoice.draftedAt)}
          </MultiTimezoneTooltip>
        ),
      },
      {
        status: 'open',
        label: 'Open',
        number: 2,
        subtext: invoice.openedAt ? (
          <MultiTimezoneTooltip timestamp={invoice.openedAt} placement="bottom">
            {formatDate(invoice.openedAt)}
          </MultiTimezoneTooltip>
        ) : undefined,
      },
      {
        status: 'sent',
        label: 'Sent',
        number: 3,
        subtext: invoice.firstSentAt ? (
          <EditablePhaseDate
            date={invoice.firstSentAt}
            onSave={handleSaveSentAt}
            label="Sent date"
            disableFuture={false}
            maxDate={DateTime.local().endOf('day')}
            maxDateMessage="Cannot be after today"
          />
        ) : undefined,
      },
      {
        status: 'paid',
        label: invoice.status === 'PARTIALLY_PAID' ? 'Partially paid' : 'Paid',
        number: 4,
        isPartial: invoice.status === 'PARTIALLY_PAID',
        subtext: invoice.paidDate ? formatDate(invoice.paidDate) : undefined,
      },
    ],
    [
      invoice.draftedAt,
      invoice.openedAt,
      invoice.firstSentAt,
      invoice.paidDate,
      invoice.status,
      handleSaveSentAt,
    ]
  );

  const totalPaid = subCurrency(invoice.total, invoice.balance);
  const hasBalance = invoice.balance.amount.greaterThan(0);
  const isDraft = invoice.status === 'DRAFT';
  const isOpen = invoice.status === 'OPEN';
  const hasLineItems = invoice.lineItemCount > 0;
  const hasSettlements = invoice.settlementCount > 0;

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [knownErrorMessage, setKnownErrorMessage] = useState<string | null>(
    null
  );

  const openInvoiceMutation = useOpenInvoice({
    invoiceKey: invoice.key,
    onSuccess: openConfirmDialog.close,
    onError: (_message, apiStatusCode) => {
      openConfirmDialog.close();
      if (IDEMPOTENT_OPEN_CODES.has(apiStatusCode)) {
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
        return;
      }
      openInvoiceMutation.reset();
      setShowSnackbar(true);
    },
  });

  const revertToDraftMutation = useRevertInvoiceToDraft({
    invoiceKey: invoice.key,
    onSuccess: revertConfirmDialog.close,
    onError: (_message, apiStatusCode) => {
      revertConfirmDialog.close();
      if (IDEMPOTENT_REVERT_CODES.has(apiStatusCode)) {
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
        return;
      }
      revertToDraftMutation.reset();
      setShowSnackbar(true);
    },
  });

  return (
    <Container maxWidth="xl">
      {/* Breadcrumbs */}
      <Stack marginTop={3} paddingX={3}>
        <SectionBreadcrumbs
          items={[
            BILLING_BREADCRUMB_ROOT,
            { label: 'Invoices', to: '/billing/invoices' },
            { label: invoice.invoiceNumber },
          ]}
        />
      </Stack>

      {/* Header */}
      <Stack paddingX={{ xs: 1, md: 3 }} spacing={1} marginTop={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h5">{invoice.invoiceNumber}</Typography>
            <InvoiceStatusChip
              status={invoice.status}
              voidReason={invoice.voidReason}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {isDraft && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditRounded />}
                  onClick={editDialog.open}
                >
                  Edit invoice
                </Button>
                <OpenInvoiceButton
                  hasLineItems={hasLineItems}
                  hasCustomer={!!invoice.customerKey}
                  hasDueDate={invoice.dueDate !== null}
                  onClick={openConfirmDialog.open}
                />
                <Button size="small" color="error" onClick={deleteDialog.open}>
                  Delete draft
                </Button>
              </>
            )}
            {isOpen && !hasSettlements && (
              <Button
                size="small"
                variant="text"
                onClick={revertConfirmDialog.open}
              >
                Revert to draft
              </Button>
            )}
            {!isDraft && invoice.status !== 'VOID' && !isSent && (
              <Button
                size="small"
                variant="outlined"
                onClick={markAsSentDialog.open}
              >
                Mark as sent
              </Button>
            )}
            {!isDraft && (
              <DownloadPDFButton
                invoiceKey={invoice.key}
                invoiceNumber={invoice.invoiceNumber}
                onError={() => setShowSnackbar(true)}
              />
            )}
            {/* Void is destructive, so render it last in the right-aligned
                row. The Stack's justify-flex-end packs every button against
                the right edge; Void lands at the far right simply by being
                the last child. */}
            {isOpen && (
              <Tooltip
                title={
                  hasSettlements ? 'Remove all settlements before voiding' : ''
                }
                arrow
              >
                <span>
                  <Button
                    size="small"
                    variant="text"
                    color="error"
                    disabled={hasSettlements}
                    onClick={voidDialog.open}
                  >
                    Void
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {invoice.customerKey && invoice.customerName ? (
            <>
              Customer:{' '}
              <Link
                to="/customers/$customerKey"
                params={{ customerKey: invoice.customerKey }}
              >
                {invoice.customerName}
              </Link>
            </>
          ) : (
            'No customer assigned'
          )}
          {invoice.project !== null && (
            <>
              {' \u00B7 '}
              Project:{' '}
              <Link
                to="/projects/$projectKey"
                params={{ projectKey: invoice.project.key }}
              >
                {invoice.project.name}
              </Link>
            </>
          )}
        </Typography>
      </Stack>

      {/* Row 1: Key details + Notes */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        paddingX={{ xs: 1, md: 3 }}
        spacing={2}
        marginTop={3}
        alignItems="stretch"
      >
        <Box flexGrow={7} flexBasis="65%">
          <Paper sx={{ padding: 3, height: '100%' }} variant="outlined">
            <PhaseIndicator
              phases={phases}
              currentStatus={currentPhase}
              terminalStatuses={TERMINAL_PHASES}
              cancelledStatus={invoice.status === 'VOID' ? 'void' : undefined}
              cancelledLabel="Void"
              cancelledReason={
                invoice.status === 'VOID' && invoice.voidedAt
                  ? `Voided ${formatDate(invoice.voidedAt)}`
                  : null
              }
            />
            <Divider sx={{ my: 3 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 3,
              }}
            >
              <AddressBlock label="Bill from" address={invoice.billFrom} />
              <BillToBlock address={invoice.billTo} />
              <Stack spacing={2} alignItems="flex-end">
                <DetailField
                  label="Invoice date"
                  value={formatDate(invoice.invoiceDate)}
                />
                <DetailField
                  label="Payment terms"
                  value={invoice.paymentTerms ?? '\u2014'}
                />
                <DetailField
                  label="Due date"
                  value={
                    invoice.dueDate ? formatDate(invoice.dueDate) : '\u2014'
                  }
                />
                <DetailField
                  label="PO number"
                  value={invoice.purchaseOrderNumber ?? '\u2014'}
                />
              </Stack>
            </Box>
          </Paper>
        </Box>

        {/* Notes (right column, Row 1). maxWidth keeps the column narrow
            so the tracker column on the left retains its established
            width; the field inside (InlineEditableField with
            `fillContainer`) fills the Paper's height within that cap. */}
        <Box flexGrow={3} flexBasis="30%" minWidth={0} maxWidth={360}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Notes
              </Typography>
              <Chip label="Internal" size="small" variant="outlined" />
            </Stack>
            <Box
              sx={{ pl: 1, flex: 1, display: 'flex', flexDirection: 'column' }}
            >
              <InlineEditableField
                value={invoice.notes}
                invoiceKey={invoice.key}
                field="notes"
                placeholder="No note set"
                rows={4}
                minHeight="9rem"
                fillContainer
              />
            </Box>
          </Paper>
        </Box>
      </Stack>

      {/* Row 2: Line items + Payments */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        paddingX={{ xs: 1, md: 3 }}
        spacing={2}
        marginTop={2}
        marginBottom={2}
        alignItems="flex-start"
      >
        <Box flexGrow={7} flexBasis="65%">
          <EditableLineItemTable
            invoice={invoice}
            lineItems={invoice.lineItems}
            lineItemsLoading={false}
          />

          {/* Memo + Payment instructions — 2-column grid under line items */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              mt: 2,
            }}
          >
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Memo
              </Typography>
              <Box sx={{ pl: 1 }}>
                <InlineEditableField
                  value={invoice.memo}
                  invoiceKey={invoice.key}
                  field="memo"
                  placeholder="No memo set"
                  minHeight="6rem"
                />
              </Box>
            </Paper>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Payment instructions
              </Typography>
              <Box sx={{ pl: 1 }}>
                <InlineEditableField
                  value={invoice.paymentInstructions}
                  invoiceKey={invoice.key}
                  field="paymentInstructions"
                  placeholder="No payment instructions set"
                  minHeight="6rem"
                />
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Payments & credits (right column, Row 2) */}
        <Box flexGrow={3} flexBasis="30%" minWidth={0} maxWidth={360}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Payments & credits
              </Typography>

              {isDraft ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 2 }}
                >
                  Payments cannot be recorded on a draft invoice
                </Typography>
              ) : (
                <>
                  <Stack spacing={1} sx={{ mb: 2 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Total paid
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatAmountWithCurrency(
                          totalPaid.amount,
                          invoice.currency
                        )}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Balance
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={hasBalance ? 'error.main' : 'success.main'}
                      >
                        {formatCurrencyValue(invoice.balance)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  {invoice.settlements.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 2 }}
                    >
                      No payments or credits recorded
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {invoice.settlements.map((settlement) => (
                        <SettlementRow
                          key={settlement.id}
                          settlement={settlement}
                        />
                      ))}
                    </Stack>
                  )}
                  {(invoice.status === 'OPEN' ||
                    invoice.status === 'PARTIALLY_PAID') && (
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      sx={{ mt: 2 }}
                      onClick={recordPaymentDialog.open}
                    >
                      Record payment
                    </Button>
                  )}
                </>
              )}
            </Paper>
            <Button
              size="small"
              onClick={auditDrawer.open}
              endIcon={<NavigateNextRounded fontSize="small" />}
              fullWidth
            >
              View audit log
            </Button>
          </Stack>
        </Box>
      </Stack>

      {/* Edit dialog — always mounted, open/close via disclosure */}
      <InvoiceEditDialog
        isOpen={editDialog.isOpen}
        onClose={editDialog.close}
        invoice={invoice}
      />

      {/* Audit drawer — always mounted, open/close via disclosure */}
      <InvoiceAuditDrawer invoiceKey={invoice.key} disclosure={auditDrawer} />

      {/* Open invoice confirmation */}
      <Dialog open={openConfirmDialog.isOpen} onClose={openConfirmDialog.close}>
        <DialogTitle>Mark invoice as open?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will mark {invoice.invoiceNumber} as open and lock the invoice
            for editing. You can revert to draft later if no payments have been
            recorded.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={openConfirmDialog.close}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => openInvoiceMutation.mutate()}
            disabled={openInvoiceMutation.isPending}
          >
            Mark as open
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revert to draft confirmation */}
      <Dialog
        open={revertConfirmDialog.isOpen}
        onClose={revertConfirmDialog.close}
      >
        <DialogTitle>Revert to draft?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will revert {invoice.invoiceNumber} back to draft status. The
            opened date will be cleared and the invoice can be edited again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={revertConfirmDialog.close}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => revertToDraftMutation.mutate()}
            disabled={revertToDraftMutation.isPending}
          >
            Revert to draft
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void invoice dialog */}
      <VoidInvoiceDialog
        isOpen={voidDialog.isOpen}
        invoice={invoice}
        onClose={voidDialog.close}
        onSuccess={voidDialog.close}
        onError={(message, apiStatusCode, httpStatusCode) => {
          // The dialog owns close decisions — 404 and known status
          // conflicts close internally; 5xx keeps the dialog open. Here
          // we only surface navigation and snackbar messaging.
          if (httpStatusCode === 404) {
            void navigate({ to: '/billing/invoices' });
            return;
          }
          if (IDEMPOTENT_VOID_CODES.has(apiStatusCode)) {
            return;
          }
          if (KNOWN_VOID_CODES.has(apiStatusCode)) {
            setKnownErrorMessage(message);
            return;
          }
          setShowSnackbar(true);
        }}
      />

      {/* Delete draft invoice dialog */}
      <DeleteDraftInvoiceDialog
        isOpen={deleteDialog.isOpen}
        invoice={invoice}
        onClose={deleteDialog.close}
        onSuccess={() => {
          void navigate({ to: '/billing/invoices' });
        }}
        onError={(message, apiStatusCode, httpStatusCode) => {
          // Dialog owns close. 404 navigates; known codes show the
          // known-error snackbar; everything else (5xx) falls through to
          // the support snackbar while the dialog stays open for retry.
          if (httpStatusCode === 404) {
            void navigate({ to: '/billing/invoices' });
            return;
          }
          if (KNOWN_DELETE_CODES.has(apiStatusCode)) {
            setKnownErrorMessage(message);
            return;
          }
          setShowSnackbar(true);
        }}
      />

      {/* Record payment dialog */}
      <RecordPaymentDialog
        isOpen={recordPaymentDialog.isOpen}
        invoice={invoice}
        onClose={recordPaymentDialog.close}
        onError={(message, apiStatusCode, httpStatusCode) => {
          // Dialog owns close. 404 navigates; known codes show the
          // known-error snackbar; 5xx falls through to support snackbar
          // while the dialog stays open.
          if (httpStatusCode === 404) {
            void navigate({ to: '/billing/invoices' });
            return;
          }
          if (KNOWN_RECORD_PAYMENT_CODES.has(apiStatusCode)) {
            setKnownErrorMessage(message);
            return;
          }
          setShowSnackbar(true);
        }}
      />

      {/* Mark as sent dialog */}
      <MarkInvoiceAsSentDialog
        isOpen={markAsSentDialog.isOpen}
        invoice={invoice}
        onClose={markAsSentDialog.close}
      />

      <KnownErrorSnackbar
        isOpen={knownErrorMessage !== null}
        onClose={() => setKnownErrorMessage(null)}
        message={knownErrorMessage ?? ''}
      />

      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message="An unexpected error occurred"
      />
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents (presentational, only used in this file)
// ---------------------------------------------------------------------------

function DetailField(props: { label: string; value: string }) {
  return (
    <Box sx={{ textAlign: 'right' }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.25 }}
      >
        {props.label}
      </Typography>
      <Typography variant="body2">{props.value}</Typography>
    </Box>
  );
}

function OpenInvoiceButton(props: {
  hasLineItems: boolean;
  hasCustomer: boolean;
  hasDueDate: boolean;
  onClick: () => void;
}) {
  const canOpen = props.hasLineItems && props.hasCustomer && props.hasDueDate;

  const missingItems: string[] = [];
  if (!props.hasLineItems) missingItems.push('At least one line item');
  if (!props.hasCustomer) missingItems.push('Customer');
  if (!props.hasDueDate) missingItems.push('Due date');

  const tooltipContent = canOpen ? (
    ''
  ) : (
    <Stack spacing={0.5}>
      <Typography variant="caption" fontWeight="bold">
        Required before opening
      </Typography>
      {missingItems.map((item) => (
        <Typography key={item} variant="caption">
          {'• ' + item}
        </Typography>
      ))}
    </Stack>
  );

  return (
    <Tooltip title={tooltipContent} arrow>
      <span>
        <Button
          size="small"
          variant="contained"
          disabled={!canOpen}
          onClick={props.onClick}
        >
          Mark as open
        </Button>
      </span>
    </Tooltip>
  );
}
